// SB6-ANA-FILT-07 (AC7 / locked decision 2) — central selection-invalidation
// reconciler.
//
// When a committed AI-class-filter change removes the currently active or
// multi-selected AI finding from the visible (server-filtered) set, that
// selection must be dropped EVERYWHERE — no stale highlighted bbox in the
// viewer, no highlighted List row, removed from the multi-select set. The
// rule is "filter wins": the viewer never keeps a sub-threshold active bbox
// alive.
//
// Implemented ONCE, centrally, here (rather than per widget) by reconciling
// `selectionStore` against the now-visible AI finding ids. Mounted in
// AnalysisFeature against the dataset-wide findings query that already
// drives the viewer overlay — that query is server-narrowed by the same
// `aiClassFilter`, so its item set IS the authoritative "passing" set.
//
// Loading-window safety: the reconciler only acts on a SETTLED, non-fetching
// query with data present. During a refetch (the debounce → request window
// after a threshold change) the previous payload is kept visible
// (`placeholderData: prev` on the findings query) and `isFetching` is true,
// so we hold off — we must not deselect a finding that is about to come back,
// nor deselect against a stale/empty in-flight set.
//
// Scope: AI findings only. A `defect`-kind active selection (DefectsPanel /
// defect tiles) is never touched — the AI threshold filter does not define
// defect visibility.
import { useEffect } from "react";
import { useSelectionStore } from "./state/selectionStore.js";

export interface FindingSelectionReconcilerArgs {
  /**
   * ids of the currently VISIBLE (server-filtered, passing) AI findings.
   * MUST be the COMPLETE passing set — a partial page would make the
   * reconciler prune selections that pass but sit beyond the page cap.
   * Callers encode completeness in `ready` (see AnalysisFeature:
   * `items.length >= total`).
   */
  visibleFindingIds: ReadonlySet<string>;
  /**
   * True only when the findings query has settled with data present, is not
   * currently re-fetching, AND `visibleFindingIds` is the complete passing
   * set (items.length >= total). The reconciler is a no-op until this is
   * true so a debounce/refetch window never prematurely deselects and a
   * page-capped partial set never wrongly prunes.
   */
  ready: boolean;
}

/**
 * Reconcile `selectionStore` against the visible AI finding ids. Clears the
 * active selection when its (ai-kind) finding is filtered out, and prunes
 * filtered-out ids from the multi-select set. Defect selections are left
 * untouched.
 */
export function useFindingSelectionReconciler({
  visibleFindingIds,
  ready,
}: FindingSelectionReconcilerArgs): void {
  useEffect(() => {
    if (!ready) return;

    const { activeFindingId, activeKind, selectedFindingIds, anchorFindingId } =
      useSelectionStore.getState();

    // Active selection: clear iff it is an AI finding that no longer passes.
    // A `defect`-kind (or unknown-kind) active selection is out of scope.
    const activeDropped =
      activeFindingId !== null &&
      activeKind === "ai" &&
      !visibleFindingIds.has(activeFindingId);

    // Multi-select set: prune any id that is no longer visible. The set holds
    // AI finding ids (Tiles/List multi-select); defect tiles don't populate
    // it. Pruning against the AI visible set is therefore safe.
    let prunedSelection: Set<string> | null = null;
    for (const id of selectedFindingIds) {
      if (!visibleFindingIds.has(id)) {
        prunedSelection ??= new Set(selectedFindingIds);
        prunedSelection.delete(id);
      }
    }

    if (!activeDropped && prunedSelection === null) return;

    useSelectionStore.setState({
      ...(activeDropped ? { activeFindingId: null, activeKind: null } : {}),
      ...(prunedSelection !== null
        ? { selectedFindingIds: prunedSelection }
        : {}),
      // Drop a dangling anchor that points at a now-hidden finding so a
      // subsequent shift-click range doesn't anchor off an invisible row.
      ...(anchorFindingId !== null && !visibleFindingIds.has(anchorFindingId)
        ? { anchorFindingId: null }
        : {}),
    });
    // Deps are intentionally just the visible set + readiness: the selection is
    // read via `getState()` (not subscribed) so the reconciler re-runs only
    // when the filtered set or readiness changes, never on a fresh user click.
  }, [visibleFindingIds, ready]);
}
